import { useEffect, type RefObject } from 'react';

// QA-28 M-07 — acessibilidade COMUM dos overlays do cardápio (quick-view, sacola, checkout, confirmação de
// mesa). Antes cada overlay só focava o "×" e escutava Escape: tabulando dentro da sacola o foco ESCAPAVA
// para o catálogo atrás do backdrop (o cliente "digitava no invisível") e, ao fechar, o foco caía no <body>
// e ele perdia o lugar na página. Este hook resolve os três pontos de uma vez:
//   1. ciclo de Tab/Shift+Tab preso aos focáveis do diálogo;
//   2. `aria-hidden` nos IRMÃOS do overlay (o conteúdo de fundo fica inerte para o leitor de tela) —
//      irmãos, não `.loja`, porque o overlay é filho de `.loja` e esconder o pai esconderia o diálogo;
//   3. devolução do foco ao elemento que abriu o overlay (WCAG 2.4.3), quando ele ainda existe.
// Fail-safe: qualquer ref vazia/DOM incompleto simplesmente não faz nada (nunca lança no render).

/** Elementos que recebem foco por Tab dentro do diálogo (ordem do DOM), ignorando os desabilitados. */
function focaveis(raiz: HTMLElement): HTMLElement[] {
  const seletor =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, details, [tabindex]:not([tabindex="-1"])';
  return Array.from(raiz.querySelectorAll<HTMLElement>(seletor)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true' && !el.hasAttribute('disabled')
  );
}

export function useDialogA11y(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const dialogo = ref.current;
    // Gatilho: quem tinha o foco quando o overlay montou (o card/botão que abriu).
    const gatilho = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialogo) return;

    // (2) Fundo inerte para leitores de tela: irmãos do overlay (o overlay é o pai do diálogo).
    const overlay = dialogo.parentElement;
    const irmaos: { el: Element; anterior: string | null }[] = [];
    if (overlay?.parentElement) {
      for (const filho of Array.from(overlay.parentElement.children)) {
        if (filho === overlay) continue;
        irmaos.push({ el: filho, anterior: filho.getAttribute('aria-hidden') });
        filho.setAttribute('aria-hidden', 'true');
      }
    }

    // (1) Foco preso: Tab no último volta ao primeiro; Shift+Tab no primeiro vai ao último.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const lista = focaveis(dialogo);
      if (lista.length === 0) return;
      const primeiro = lista[0];
      const ultimo = lista[lista.length - 1];
      const atual = document.activeElement;
      // Foco fora do diálogo (backdrop, catálogo de fundo): traz de volta para dentro.
      if (!(atual instanceof HTMLElement) || !dialogo.contains(atual)) {
        e.preventDefault();
        (e.shiftKey ? ultimo : primeiro).focus();
        return;
      }
      if (!e.shiftKey && atual === ultimo) {
        e.preventDefault();
        primeiro.focus();
      } else if (e.shiftKey && atual === primeiro) {
        e.preventDefault();
        ultimo.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      for (const { el, anterior } of irmaos) {
        if (anterior == null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', anterior);
      }
      // (3) Devolve o foco ao gatilho — só se ele ainda estiver no documento (o card pode ter desmontado).
      if (gatilho && document.contains(gatilho)) gatilho.focus();
    };
  }, [ref]);
}
